import { inject, Injectable, InjectionToken } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { PaginatedStateEffects } from '@shared/ui';
import type { ICountAlgorithmsQuery, IListAlgorithmsQuery, IRetrieveAlgorithmQuery } from '@protocols/use-cases';
import { Algorithm, type AlgorithmRepository, Engine, ExecutionEnvironment } from '@telespot/domain';
import { combineLatest, from, Observable, of } from 'rxjs';
import { skip, switchMap, take, tap } from 'rxjs/operators';
import { AlgorithmData, AlgorithmState, AlgorithmStateModel } from '../state/algorithm.state';

export const COUNT_ALGORITHMS_QUERY = new InjectionToken<ICountAlgorithmsQuery>('COUNT_ALGORITHMS_QUERY');
export const LIST_ALGORITHMS_QUERY = new InjectionToken<IListAlgorithmsQuery>('LIST_ALGORITHMS_QUERY');
export const RETRIEVE_ALGORITHM_QUERY = new InjectionToken<IRetrieveAlgorithmQuery>('RETRIEVE_ALGORITHM_QUERY');

@Injectable()
export class AlgorithmEffects extends PaginatedStateEffects<AlgorithmData, AlgorithmStateModel> {
  private readonly countAlgorithmsQuery = inject<ICountAlgorithmsQuery>(COUNT_ALGORITHMS_QUERY);
  private readonly listAlgorithmsQuery = inject<IListAlgorithmsQuery>(LIST_ALGORITHMS_QUERY);
  private readonly retrieveAlgorithmQuery = inject<IRetrieveAlgorithmQuery>(RETRIEVE_ALGORITHM_QUERY);

  constructor(
    protected readonly algorithmState: AlgorithmState,
  ) {
    super(algorithmState);
    this.filterByEnvironment(this.algorithmState.environmentFilter$.pipe(skip(1)));
    this.includeDeprecatedEffect(this.algorithmState.includeDeprecated$.pipe(skip(1)));
  }

  // ============================================================================
  // Re-exposed selectors from AlgorithmState
  // ============================================================================

  public readonly environmentFilter$ = this.algorithmState.environmentFilter$;
  public readonly includeDeprecated$ = this.algorithmState.includeDeprecated$;

  // ============================================================================
  // Re-exposed updaters from AlgorithmState
  // ============================================================================

  public readonly setEnvironmentFilter = this.algorithmState.setEnvironmentFilter;
  public readonly setIncludeDeprecated = this.algorithmState.setIncludeDeprecated;
  public readonly setSearchTerm = this.algorithmState.setSearchTerm;
  public readonly changePageSize = this.algorithmState.changePageSize;
  public readonly removeItem = this.algorithmState.removeItem;

  // ============================================================================
  // Effects
  // ============================================================================

  public readonly filterByEnvironment = this.effect((env$: Observable<ExecutionEnvironment | undefined>) =>
    env$.pipe(
      concatLatestFrom(() => [this.algorithmState.searchTerm$]),
      tap(([, searchTerm]) => {
        this.countItems();
        this.listItems({
          searchTerm,
          lastItem: undefined,
        });
      })
    )
  );

  public readonly includeDeprecatedEffect = this.effect((includeDeprecated$: Observable<boolean>) =>
    includeDeprecated$.pipe(
      concatLatestFrom(() => [this.algorithmState.searchTerm$]),
      tap(([, searchTerm]) => {
        this.countItems();
        this.listItems({
          searchTerm,
          lastItem: undefined,
        });
      })
    )
  );

  public readonly reloadSingleAlgorithm = this.effect((algorithmId$: Observable<string>) =>
    algorithmId$.pipe(
      tap(() => this.algorithmState.setLoading(true)),
      switchMap(async (algorithmId: string) => {
        const algorithm = await this.loadAlgorithm(algorithmId);
        this.algorithmState.updateItemAndFinishLoading({ id: algorithmId, changes: algorithm });
      })
    )
  );

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  /**
   * Count items via the count algorithms query, applying the current
   * environment filter and includeDeprecated state.
   * @param searchTerm Optional search term to filter items
   * @returns Promise resolving to the total count
   */
  protected async countRepository(searchTerm?: string): Promise<number> {
    const snapshot = await combineLatest([
      this.algorithmState.environmentFilter$,
      this.algorithmState.includeDeprecated$,
    ])
      .pipe(take(1))
      .toPromise();
    if (!snapshot) return 0;
    const [environmentFilter, includeDeprecated] = snapshot;
    return this.countAlgorithmsQuery.execute({ searchTerm, environmentFilter, includeDeprecated });
  }

  /**
   * Delete repository item.
   * The algorithm store does not support deletion.
   * Returning false will invalidate any posterior item removal operations.
   * @param _itemId The ID of the item to remove (not used in this case)
   * @returns Promise that lets you know if the operation was successful, always false in this case
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async deleteRepositoryItem(_itemId: string): Promise<boolean> {
    return false;
  }

  protected listFlow(
    limit: number,
    lastItem: AlgorithmData | undefined,
    searchTerm: string | undefined
  ): Observable<AlgorithmData[]> {
    return combineLatest([this.algorithmState.environmentFilter$, this.algorithmState.includeDeprecated$]).pipe(
      take(1),
      switchMap(([environmentFilter, includeDeprecated]) =>
        from(
          this.listAlgorithmsQuery.execute({
            searchTerm,
            limit,
            lastItemName: lastItem?.name,
            environmentFilter,
            includeDeprecated,
          })
        )
      )
    );
  }

  /**
   * No counts/metadata are needed to be loaded for the algorithm store.
   * In this case, deferred loading happens on demand.
   * @param _itemId The ID of the item to load counts for
   * @returns Observable resolving to the changes to be applied to the item.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected processDeferredAttributes(_itemId: string): Observable<Partial<AlgorithmData>> {
    return of({});
  }

  // ============================================================================
  // Public helper methods
  // ============================================================================

  public async loadAlgorithm(algorithmId: string): Promise<AlgorithmData> {
    const algorithm = await this.retrieveAlgorithmQuery.execute(algorithmId);
    return {
      id: algorithm.id,
      name: algorithm.name,
      version: algorithm.version,
      description: algorithm.description,
      regulated: algorithm.regulated,
      deprecated: algorithm.deprecated,
      environment: algorithm.environment,
      numOrganizations: algorithm.numOrganizations,
      numWorkspaces: algorithm.numWorkspaces,
    };
  }
}
